import { TextFieldProps } from '@material-ui/core'
import Fuse from 'fuse.js'
import React, { useEffect, useState } from 'react'

import { Search, SearchProps } from '../Search'
import { SearchItem } from '../Search/SearchBase'

type FuzzySearchProps<T extends SearchItem> = Omit<
  SearchProps<T>,
  'search' | 'results' | 'onSearchChange' | 'onSelectItem' | 'formatResults'
> & {
  list: T[]
  options: Fuse.IFuseOptions<T>
  onSelectItem: (
    item: Fuse.FuseResult<T>['item'],
    result?: Fuse.FuseResult<T>,
  ) => void
  onClear?: () => void
  formatResults: (
    item: Fuse.FuseResult<T>['item'],
    result?: Fuse.FuseResult<T>,
  ) => React.ReactNode
  keyBy: string
  maxResults?: number
  dropdown?: boolean
  /** ID of list item to select on load/blur, as implied by `keyBy`. Requires `list` to be set */
  defaultValue?: string
}

function FuzzySearch<T extends SearchItem>({
  list,
  options,
  onSelectItem,
  onClear,
  formatResults,
  keyBy,
  defaultValue,
  maxResults = 5,
  dropdown = false,
  ...textFieldProps
}: FuzzySearchProps<T>): JSX.Element {
  const [search, setSearch] = useState('')
  const [fuse, setFuse] = useState(new Fuse<T>(list, options))
  const results = fuse.search(search).slice(0, maxResults)

  /** Update fuse if the props change. Mostly useful in development */
  useEffect(() => {
    setFuse(new Fuse<T>(list, options))
  }, [list, options])

  return (
    <>
      <Search
        search={search}
        list={list.map((item, refIndex) => ({
          item,
          refIndex,
        }))}
        results={results}
        keyBy={`item.${keyBy}`}
        maxResults={maxResults}
        formatResults={(result) => formatResults(result.item, result)}
        onSearchChange={(search) => setSearch(search)}
        onSelectItem={(result) => onSelectItem(result.item, result)}
        onClear={onClear}
        dropdown={dropdown}
        defaultValue={defaultValue}
        {...textFieldProps}
      />
    </>
  )
}

export default FuzzySearch
